import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from 'components/PageLayout';
import LoadingState from 'components/LoadingState';
import ErrorState from 'components/ErrorState';
import { FinalistOverview } from 'components/FinalistList';
import { apiRequest } from 'services/apiConfig';
import { useAuth } from 'contexts/AuthContext';
import AccessDenied from 'components/AccessDenied';
import NotAuthenticated from 'components/NotAuthenticated';
import { useRoleInfo } from 'hooks/useRoleInfo';
import YearSelectorBar from 'components/YearSelectorBar';

const FinalistListPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const currentYear = new Date().getFullYear();
  const EARLIEST_YEAR = 2025;

  const [year, setYear] = useState<number>(currentYear);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const {
    validUserCategories,
    userIsSuperAdmin,
    userIsCategoryJudge,
    userIsPillarPIC,
  } = useRoleInfo();

  const userHasAccess =
    (userIsCategoryJudge && validUserCategories.length > 0) || userIsSuperAdmin;

  // Fetch finalist submissions on component mount
  useEffect(() => {
    fetchFinalistSubmissions(year);
  }, [year]);

  const fetchFinalistSubmissions = async (fetchYear: number = year) => {
    try {
      setLoading(true);
      setError(null);

      // Create a custom API call for final submissions instead of using getQualificationSubmissions
      let allData: any[] = [];
      let page = 1;
      let hasMorePages = true;
      const pageSize = 100;

      while (hasMorePages) {
        const response = await apiRequest(
          `/award-submissions?filters[competition_stage][$eq]=final&filters[createdAt][$gte]=${fetchYear}-01-01T00:00:00.000Z&filters[createdAt][$lte]=${fetchYear}-12-31T23:59:59.999Z&populate=*&pagination[page]=${page}&pagination[pageSize]=${pageSize}&pagination[withCount]=true`,
          {
            method: 'GET',
          }
        );

        const result = await response.json();

        if (!result.data || !Array.isArray(result.data)) {
          throw new Error(
            `Invalid award-submissions data received from API (page ${page})`
          );
        }

        // Add current page data to total
        allData = allData.concat(result.data);

        // Check pagination metadata for more pages
        const pagination = result.meta?.pagination;
        if (pagination) {
          hasMorePages = page < pagination.pageCount;
        } else {
          hasMorePages = false;
        }

        page++;
      }

      setSubmissions(allData);

      if (allData.length === 0) {
        setError('No finalist submissions found');
      }
    } catch (err: any) {
      console.error('❌ Error fetching finalist submissions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Check access control first
  if (!isAuthenticated()) {
    return (
      <PageLayout
        title="Award Finalists"
        subtitle="Celebrating excellence across all pillars"
      >
        <NotAuthenticated
          message="Please log in to view finalist submissions."
          buttonLink="/login"
        />
      </PageLayout>
    );
  }

  if (!userHasAccess) {
    return (
      <PageLayout
        title="Award Finalists"
        subtitle="Celebrating excellence across all pillars"
      >
        <AccessDenied
          message={
            userIsPillarPIC
              ? 'Pillar PICs do not have access to view finalists. Only category judges can view finalist submissions.'
              : "You don't have permission to view finalist submissions. Access is restricted to authorized category judges only."
          }
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Award Finalists"
      subtitle="In the arena of excellence, may the finest effort claim victory"
    >
      <YearSelectorBar
        year={year}
        setYear={setYear}
        earliestYear={EARLIEST_YEAR}
        currentYear={currentYear}
      />

      {loading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingState message="Loading finalist submissions..." />
        </div>
      ) : error ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <ErrorState
            title="No Finalists Available"
            error={error}
            onRetry={() => fetchFinalistSubmissions(year)}
          />
        </div>
      ) : (
        <FinalistOverview
          submissions={submissions}
          userCategory="All Categories"
        />
      )}
    </PageLayout>
  );
};

export default FinalistListPage;
